import { Component } from 'react';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
import * as Sentry from '@sentry/react';
import { connect } from 'react-redux';
import { isIOS } from 'react-device-detect';
import { setSync } from '../../actionCreators/appActions';
import { setEssences } from '../../actionCreators/treesActions';
import { setPhotosGalleries } from '../../actionCreators/elementsActions';
// Utils
import { getOfflineServices } from '../../utils/offline/OfflineServices';
import { isOfflineEnabled, setOfflineFlagContext } from '../../utils/offline/OfflineFlag';
import { createSyncEngine } from '../../utils/offline/SyncEngine';
import { emitSyncEvent } from '../../utils/offline/SyncEvents';
import { setPendingKeys } from '../../utils/offline/SyncState';
import { getHandlers } from '../../utils/offline/handlers';
import { showToast } from '../../utils/ToastsUtil';
import RealtimeUtil from '../../utils/RealtimeUtil';
import FileInfosUtil from '../../utils/FileInfosUtil';
import Connectivity from '../../utils/offline/Connectivity';

// Relie le moteur de synchro à l'application (sans rendu) : démarrage pour le pilote ou tant que la file n'est pas vide,
// compteurs de la file dans la clé Redux « sync », relais des évènements, signalement des échecs
class SyncManager extends Component {
    render() {
        return null;
    }

    componentDidMount = () => {
        this.refresh();
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    componentDidUpdate = (prevProps) => {
        const { activeOrganization, loginAsData, logged, isOnline, pilotLoadedAt } = this.props;
        // pilotLoadedAt : la liste des organisations du pilote vient d'arriver
        if (prevProps.activeOrganization !== activeOrganization || prevProps.loginAsData !== loginAsData || prevProps.logged !== logged
            || prevProps.pilotLoadedAt !== pilotLoadedAt) this.refresh();
        // Retour du réseau : envoi immédiat, attentes de reprise annulées
        if (!prevProps.isOnline && isOnline) this.engine?.trigger({ force: true });
    }

    componentWillUnmount = () => {
        clearTimeout(this.countsTimeout);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.stopEngine();
    }

    // App revenue au premier plan (minuteries ralenties en arrière-plan sur mobile) : relance l'envoi
    handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') this.engine?.trigger({ force: true });
    }

    // Démarre ou arrête le moteur : le drapeau ne décide que des nouvelles saisies, une file non vide est toujours envoyée ;
    // jamais en « se connecter en tant que »
    refresh = async () => {
        setOfflineFlagContext({ activeOrganization: this.props.activeOrganization, loginAsData: this.props.loginAsData });
        if (!this.props.logged || this.props.loginAsData) return this.stopEngine();
        // Base locale illisible : moteur laissé tel quel ; jamais démarré, les saisies partent directement à l'API comme hors pilote
        const queued = await getOfflineServices()?.outbox.counts().then(counts => counts.total, () => null);
        if (queued === null) return;
        if (isOfflineEnabled() || queued > 0) this.startEngine();
        else this.stopEngine();
    }

    // Crée et démarre le moteur de l'utilisateur courant (rien si déjà fait)
    startEngine = () => {
        const services = getOfflineServices();
        if (!services || (this.engine && services.engine === this.engine)) return;
        this.stopEngine();
        this.services = services;
        this.engine = createSyncEngine({
            outbox: services.outbox, idMap: services.idMap, snapshot: services.snapshot, syncLog: services.syncLog,
            extras: { photos: services.photos },
            handlers: getHandlers(),
            // Axios global : intercepteurs de Connectivity et de RealtimeUtil (X-Connection-Id) compris
            transport: (config, options = {}) => Axios({ ...config, ...options }),
            getAuthToken: () => new Cookies().get('token'),
            // Paramètre connectionId des modifications d'éléments (l'auteur est surtout reconnu par l'en-tête X-Connection-Id)
            getConnectionId: () => RealtimeUtil.getConnectionId(),
            isApiReachable: () => Connectivity.probe(),
            onEvent: this.handleEngineEvent
        });
        services.engine = this.engine;
        this.unsubscribeOutbox = services.outbox.subscribe(this.refreshCounts);
        this.engine.start();
        this.refreshCounts();
        // Purge en ligne seulement : sans réseau, une copie peut servir des semaines
        if (Connectivity.isOnline()) this.purgeOldSnapshots(services).catch(console.error);
        navigator.storage?.persist?.().catch(() => { });
        // Safari efface les données d'un site non visité pendant 7 jours, sauf application sur l'écran d'accueil
        if (isIOS && !navigator.standalone && !localStorage.getItem('grality-install-hint-shown')) {
            localStorage.setItem('grality-install-hint-shown', '1');
            showToast('install_to_home_screen');
        }
    }

    // Copies non ouvertes depuis 30 jours, sans saisie en attente et hors projet ouvert
    purgeOldSnapshots = async (services) => {
        const limit = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const [records, ops] = await Promise.all([services.snapshot.listProjects(), services.outbox.list()]);
        const projectIdsWithOps = new Set(ops.map(op => Number(op.projectId)));
        for (const record of records)
            if (new Date(record.lastOpenedAt).getTime() < limit && !projectIdsWithOps.has(record.id) && !services.snapshot.isActive(record.id))
                await services.snapshot.deleteProject(record.id);
    }

    // Arrête le moteur et vide la clé « sync »
    stopEngine = () => {
        this.unsubscribeOutbox?.();
        this.unsubscribeOutbox = null;
        if (this.engine) {
            this.engine.stop();
            if (this.services?.engine === this.engine) this.services.engine = null;
            this.props.setSync(null);
        }
        this.engine = null;
        this.services = null;
        setPendingKeys([]);
    }

    // Compteurs de la file dans le store, au plus toutes les 300 ms
    refreshCounts = () => {
        this.refreshPendingKeys();
        clearTimeout(this.countsTimeout);
        this.countsTimeout = setTimeout(async () => {
            if (!this.services) return;
            const counts = await this.services.outbox.counts();
            this.props.setSync({ ...counts, lastSyncAt: this.lastSyncAt || null });
        }, 300);
    }

    // Clés en attente (SyncState) sans délai, contrairement aux compteurs ; une lecture plus ancienne arrivée après est ignorée
    refreshPendingKeys = async () => {
        const services = this.services;
        if (!services) return;
        const read = this.pendingKeysRead = (this.pendingKeysRead || 0) + 1;
        const ops = await services.outbox.list();
        if (this.services === services && read === this.pendingKeysRead) setPendingKeys(ops.flatMap(op => op.touches || []));
    }

    // Relaie un évènement du moteur ; un échec que personne n'attendait est signalé (toast, Sentry)
    handleEngineEvent = (event) => {
        emitSyncEvent(event);
        if (event.type === 'op-acked') this.lastSyncAt = new Date().toISOString();
        if (['queue-changed', 'op-acked', 'op-failed', 'op-retry'].includes(event.type)) this.refreshCounts();
        if (event.op?.type === 'essence.create' && ['op-acked', 'op-discarded'].includes(event.type)) this.replaceLocalEssence(event);
        if (event.type === 'op-acked' && event.op.type === 'photo.upload' && event.result?.fileInfo) this.replaceLocalPhoto(event.result);
        if (event.type === 'op-failed' && !event.attended) {
            showToast('sync_failed');
            // Type d'opération et statut seulement, jamais le contenu de la saisie
            Sentry.captureMessage('Échec de synchronisation hors-ligne', {
                level: 'warning',
                tags: { offlineOpType: event.op?.type, offlineStatus: String(event.error?.status ?? 'none') },
                extra: { reason: event.error?.reason, message: event.error?.message, attempts: event.op?.attempts }
            });
        }
    }

    // Photo envoyée : la version du serveur remplace celle affichée depuis le fichier local
    replaceLocalPhoto = ({ fileInfo, localId }) => {
        this.props.setPhotosGalleries(current => {
            const index = current?.findIndex(photo => photo.id === localId) ?? -1;
            if (index === -1) return current;
            if (current[index].url?.startsWith('blob:')) URL.revokeObjectURL(current[index].url);
            const photosGalleries = [...current];
            photosGalleries[index] = { ...fileInfo, url: FileInfosUtil.getUrl(fileInfo) };
            return photosGalleries;
        });
    }

    // Essence temporaire (id négatif) déjà dans la liste : remplacée par celle du serveur, dans Redux et dans le cache
    replaceLocalEssence = async (event) => {
        const localId = event.op.target.id;
        const localEssence = this.props.essences?.find(essence => essence.id === localId);
        if (!localEssence) return;
        const serverEssences = event.type === 'op-acked' ? event.result?.essences || []
            : [{ ...localEssence, id: await getOfflineServices()?.idMap.get('essence', localId) }].filter(essence => essence.id);
        const essences = [...this.props.essences.filter(essence => essence.id !== localId), ...serverEssences]
            .filter((essence, index, list) => list.findIndex(e => e.id === essence.id) === index);
        this.props.setEssences(essences);
        try { localStorage.setItem('essences', JSON.stringify(essences)); }
        catch (error) { console.error(error); }
    }
}

const mapStateToProps = (state) => {
    return {
        essences: state.essences,
        photosGalleries: state.photosGalleries,
        activeOrganization: state.activeOrganization,
        loginAsData: state.loginAsData,
        isOnline: state.isOnline
    };
};

const mapDispatchToProps = {
    setSync,
    setEssences,
    setPhotosGalleries
};

export default connect(mapStateToProps, mapDispatchToProps)(SyncManager);
